import { Button, cn, Input, PageContainer, PageHeader, PageHeaderActionPrimary, PageHeaderActions, PageHeaderActionSecondary, PageHeaderLeading, PageHeaderStack, Skeleton, Textarea, useDeclareUiSurface } from '@yeshuman/sdk'
import { Loader2, Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'


import { SlidePreview } from './slide-preview'
import {
  usePublishSlideDeck,
  useSlideDeck,
  useUpdateSlideDeck,
  type SlideRow,
  type SlideWrite,
} from './use-slides'

export function SlideEditorPage() {
  const { id } = useParams<{ id: string }>()
  const { data: deck, isLoading, isError, refetch } = useSlideDeck(id)
  const updateDeck = useUpdateSlideDeck(id ?? '')
  const publishDeck = usePublishSlideDeck(id ?? '')

  const [title, setTitle] = useState('')
  const [slides, setSlides] = useState<SlideWrite[]>([])
  const [selected, setSelected] = useState(0)

  useDeclareUiSurface(
    id
      ? { intent: 'edit', entity: 'slide_deck', id, mode: 'page', fields: ['title', 'html', 'css'] }
      : null,
  )

  useEffect(() => {
    if (!deck) return
    setTitle(deck.title)
    setSlides(
      deck.slides.map((slide: SlideRow) => ({
        id: slide.id,
        order: slide.order,
        html: slide.html,
        css: slide.css,
      })),
    )
    setSelected((current) => Math.min(current, Math.max(0, deck.slides.length - 1)))
  }, [deck])

  const current = slides[selected]

  const previewHtml = current?.html ?? ''
  const previewCss = current?.css ?? ''

  const canPublish = useMemo(
    () => slides.some((slide) => (slide.html ?? '').trim().length > 0),
    [slides],
  )

  async function handleSave() {
    if (!id) return
    await updateDeck.mutateAsync({
      title,
      slides: slides.map((slide, index) => ({ ...slide, order: index })),
    })
    toast.success('Saved')
  }

  async function handlePublish() {
    if (!id) return
    await handleSave()
    const published = await publishDeck.mutateAsync()
    toast.success('Published')
    const url = `${window.location.origin}/s/${published.public_slug}`
    await navigator.clipboard.writeText(url).catch(() => undefined)
  }

  function addSlide() {
    setSlides((prev) => [...prev, { html: '', css: '', order: prev.length }])
    setSelected(slides.length)
  }

  if (!id) {
    return <Navigate to="/slides" replace />
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="mb-6 h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </PageContainer>
    )
  }

  if (isError || !deck) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-sm text-muted-foreground">This deck could not be opened.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/slides">Back to slides</Link>
          </Button>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeaderStack>
        <PageHeader
          title={title.trim() || 'Untitled deck'}
          description={deck.status === 'published' ? `Published · /s/${deck.public_slug}` : 'Draft'}
          leading={<PageHeaderLeading back={{ label: 'Slides', to: '/slides' }} />}
          actions={
            <PageHeaderActions
              secondary={
                <PageHeaderActionSecondary onClick={() => void handleSave()} disabled={updateDeck.isPending}>
                  {updateDeck.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  Save
                </PageHeaderActionSecondary>
              }
              primary={
                <PageHeaderActionPrimary
                  onClick={() => void handlePublish()}
                  disabled={!canPublish || publishDeck.isPending}
                >
                  {publishDeck.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  Publish
                </PageHeaderActionPrimary>
              }
            />
          }
        />
      </PageHeaderStack>

      <div className="mb-4">
        <label className="mb-1.5 block text-sm text-muted-foreground" htmlFor="slide-deck-title">
          Title
        </label>
        <Input
          id="slide-deck-title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Untitled deck"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[14rem_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Slides</p>
          {slides.map((slide, index) => (
            <button
              key={slide.id ?? `new-${index}`}
              type="button"
              onClick={() => setSelected(index)}
              className={cn(
                'rounded-md px-3 py-2 text-left text-sm',
                selected === index ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50',
              )}
            >
              Slide {index + 1}
            </button>
          ))}
          <Button variant="outline" size="sm" onClick={addSlide}>
            <Plus data-icon="inline-start" />
            Add slide
          </Button>
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <label className="text-sm text-muted-foreground" htmlFor="slide-html">
            HTML
          </label>
          <Textarea
            id="slide-html"
            value={current?.html ?? ''}
            onChange={(event) => {
              const html = event.target.value
              setSlides((prev) => prev.map((slide, index) => (index === selected ? { ...slide, html } : slide)))
            }}
            className="min-h-48 font-mono text-sm"
          />
          <label className="text-sm text-muted-foreground" htmlFor="slide-css">
            CSS
          </label>
          <Textarea
            id="slide-css"
            value={current?.css ?? ''}
            onChange={(event) => {
              const css = event.target.value
              setSlides((prev) => prev.map((slide, index) => (index === selected ? { ...slide, css } : slide)))
            }}
            className="min-h-24 font-mono text-sm"
          />
        </div>

        <div className="flex min-h-80 min-w-0 flex-col gap-2">
          <p className="text-sm text-muted-foreground">Preview</p>
          <SlidePreview html={previewHtml} css={previewCss} title={`Preview slide ${selected + 1}`} />
        </div>
      </div>
    </PageContainer>
  )
}
